import { useEffect, useState } from "react";
import { GetUserTransactions, ReverifyPayment } from "../api/api";
import ErrorToast from "./toasts/ErrorToast";
import SuccessToast from "./toasts/SuccessToast";
import "./TransactionsList.css";

const TransactionsList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reverifyingId, setReverifyingId] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 8,
    total: 0,
  });
  const [page, setPage] = useState(1);

  const fetchTransactions = async (pageNum = 1) => {
    setLoading(true);
    try {
      const { data } = await GetUserTransactions({
        page: pageNum,
        per_page: 8,
      });
      setTransactions(data.transactions || []);
      setPagination(
        data.pagination || {
          current_page: 1,
          last_page: 1,
          per_page: 8,
          total: 0,
        },
      );
    } catch (error) {
      console.error("خطا در دریافت تراکنش‌ها:", error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  const handleReverify = async (paymentId) => {
    setReverifyingId(paymentId);
    try {
      const { data } = await ReverifyPayment({ payment_id: paymentId });

      if (data.code == 100 || data.code == 101) {
        SuccessToast(data.message || "استعلام مجدد با موفقیت انجام شد");
      } else if (data.code == -51) {
        ErrorToast(data.message || "پرداخت یافت نشد");
      } else {
        ErrorToast(data.message || "استعلام مجدد ناموفق بود");
      }
      
    } catch (error) {
      if (error.response?.data?.message) {
        ErrorToast(error.response.data.message);
      } else {
        ErrorToast("خطا در ارتباط با سرور");
      }
    } finally {
      setReverifyingId(null);
      await fetchTransactions(pagination.current_page);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getStatusClass = (statusCode) => {
    // موفق
    if (statusCode == 100 || statusCode == 101) {
      return { class: "status-success", icon: "bx bx-check", text: "موفق" };
    }
    // در انتظار پرداخت
    if (statusCode == 0) {
      return { class: "status-pending", icon: "bx bx-time", text: "معلق" };
    }
    // پرداخت یافت نشد
    if (statusCode == -51) {
      return { class: "status-canceled", icon: "bx bx-info-circle", text: "ناموفق" };
    }
    // لغو شده توسط کاربر
    if (statusCode == -1) {
      return { class: "status-canceled", icon: "bx bx-info-circle", text: "ناموفق" };
    }
    // خطا در ارتباط با درگاه
    if (statusCode == -500) {
      return { class: "status-canceled", icon: "bx bx-info-circle", text: "ناموفق" };
    }
    // سایر خطاها
    return { class: "status-canceled", icon: "bx bx-info-circle", text: "ناموفق" };
  };

  // بررسی قابلیت استعلام مجدد - فقط وضعیت 0 (در انتظار پرداخت)
  const canReverify = (statusCode, authority) => {
    return statusCode == 0 && authority && authority != null;
  };

  const handlePrevPage = () => {
    if (pagination.current_page > 1) {
      setPage(pagination.current_page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.current_page < pagination.last_page) {
      setPage(pagination.current_page + 1);
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">در حال بارگذاری...</span>
        </div>
      </div>
    );
  }

  if (transactions.length == 0) {
    return (
      <div className="empty-state">
        هنوز تراکنشی ثبت نشده است
      </div>
    );
  }

  return (
    <div className="transactions-list">
      <div className="transactions-header">
        <h6>📜 لیست تراکنش‌ها</h6>
        <small>مجموع {pagination.total} تراکنش</small>
      </div>

      <div className="table-responsive">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>مبلغ (تومان)</th>
              <th>وضعیت</th>
              <th>کد پیگیری</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => {
              const status = getStatusClass(tx.status_code);
              const showReverify = canReverify(tx.status_code, tx.authority);
              return (
                <tr key={tx.id}>
                  <td>{formatDate(tx.created_at)}</td>
                  <td>{Number(tx.amount).toLocaleString() || "-"}</td>
                  <td>
                    <span className={`status-badge ${status.class}`}>
                      <i className={status.icon}></i>
                      {status.text}
                    </span>
                   </td>
                  <td>{tx.ref_id || "-"} </td>
                  <td>
                    {showReverify && (
                      <button
                        className="btn-reverify"
                        onClick={() => handleReverify(tx.id)}
                        disabled={reverifyingId == tx.id}
                      >
                        {reverifyingId == tx.id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <>
                            <i className="bx bx-refresh"></i>
                            استعلام
                          </>
                        )}
                      </button>
                    )}
                   </td>
                 </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.last_page > 1 && (
        <div className="pagination-wrapper">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={pagination.current_page <= 1}
          >
            <i className="bx bx-chevron-right"></i>
            قبلی
          </button>
          <span className="pagination-info">
            صفحه {pagination.current_page} از {pagination.last_page}
          </span>
          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={pagination.current_page >= pagination.last_page}
          >
            بعدی
            <i className="bx bx-chevron-left"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionsList;